/**
 * NotificationsPage — page composition that wraps the NotificationPanel
 * component with NotificationsContext data and API integration.
 *
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../state/notifications-context';
import { NotificationPanel } from '../components/NotificationPanel';
import type { NotificationEntry } from '../components/NotificationPanel';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildResourceLink(notification: {
  resourceType: string;
  resourceId: string;
  boardId: string;
}): string {
  if (notification.resourceType === 'card') {
    return `/boards/${notification.boardId}/cards/${notification.resourceId}`;
  }
  return `/boards/${notification.boardId}`;
}

function formatTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationsPage(): React.ReactElement {
  const { state, markRead, markAllRead, fetchNotifications } = useNotifications();

  if (state.isLoading && state.notifications.length === 0) {
    return (
      <main className="usa-section padding-x-4">
        <div className="usa-prose" aria-busy="true" aria-label="Loading notifications">
          <p className="text-base">Loading notifications…</p>
        </div>
      </main>
    );
  }

  if (state.error && state.notifications.length === 0) {
    return (
      <main className="usa-section padding-x-4">
        <div className="usa-alert usa-alert--error" role="alert">
          <div className="usa-alert__body">
            <p className="usa-alert__text">{state.error}</p>
          </div>
        </div>
        <button
          type="button"
          className="usa-button margin-top-2"
          onClick={() => { fetchNotifications(); }}
        >
          Retry
        </button>
      </main>
    );
  }

  // Sort notifications in reverse chronological order (newest first)
  const sortedNotifications = [...state.notifications].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  // Map to the NotificationPanel entry format
  const panelEntries: NotificationEntry[] = sortedNotifications.map((n) => ({
    notificationId: n.notificationId,
    message: n.message,
    actorName: n.actorName,
    read: n.read,
    createdAt: n.createdAt,
  }));

  return (
    <main className="usa-section padding-x-4">
      <h1 className="font-heading-xl margin-bottom-3">Notifications</h1>

      <NotificationPanel
        notifications={panelEntries}
        unreadCount={state.unreadCount}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
      />

      {/* Extended notification detail list with type, links, and timestamps */}
      {sortedNotifications.length > 0 && (
        <section aria-label="Notification details" className="margin-top-4">
          <ul className="usa-list usa-list--unstyled">
            {sortedNotifications.map((notification) => (
              <li
                key={notification.notificationId}
                className={`padding-2 margin-bottom-1 border-left-05 radius-sm ${
                  notification.read
                    ? 'border-base-light bg-base-lightest'
                    : 'border-primary bg-primary-lighter'
                }`}
              >
                <div className="display-flex flex-align-center flex-justify">
                  <span className="usa-tag text-no-uppercase font-sans-3xs">
                    {notification.type}
                  </span>
                  <time
                    dateTime={notification.createdAt}
                    className="font-sans-3xs text-base"
                  >
                    {formatTimestamp(notification.createdAt)}
                  </time>
                </div>
                <p className="margin-top-05 margin-bottom-05 font-sans-xs">
                  <strong>{notification.actorName}</strong> {notification.message}
                </p>
                <Link
                  to={buildResourceLink(notification)}
                  className="font-sans-3xs text-primary"
                  aria-label={`View related ${notification.resourceType}`}
                >
                  View {notification.resourceType} →
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
